import React, { useState, useEffect } from 'react';
import { Button } from 'components/Button';
import { isRunningAsPWA } from 'utils/serviceWorkerRegistration';
import classNames from 'classnames';
import './index.css';

const PWAInstallPrompt = ({
  className,
  showOnMobile = true,
  showOnDesktop = true,
  autoHide = true,
  hideDelay = 5000,
  onInstallSuccess,
  onInstallCancel,
  onInstallError,
  ...props
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Detect device type
  useEffect(() => {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    setDeviceType(isMobile ? 'mobile' : 'desktop');
  }, []);

  // Check if app is already installed
  useEffect(() => {
    const checkInstalled = () => {
      const isPWA = isRunningAsPWA();
      setIsInstalled(isPWA);

      if (isPWA) {
        setIsVisible(false);
      }
    };

    checkInstalled();

    // Listen for app installed event
    window.addEventListener('appinstalled', checkInstalled);

    return () => {
      window.removeEventListener('appinstalled', checkInstalled);
    };
  }, []);

  // Handle beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = e => {
      console.log('PWA: beforeinstallprompt event fired');

      // Prevent the mini-infobar from appearing
      e.preventDefault();

      // Store the event for later use
      setDeferredPrompt(e);

      // Show install prompt based on device type
      const shouldShow =
        (deviceType === 'mobile' && showOnMobile) ||
        (deviceType === 'desktop' && showOnDesktop);

      if (shouldShow && !isInstalled) {
        setCanInstall(true);
        setIsVisible(true);

        // Auto-hide after delay
        if (autoHide) {
          setTimeout(() => {
            setIsVisible(false);
          }, hideDelay);
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [deviceType, showOnMobile, showOnDesktop, autoHide, hideDelay, isInstalled]);

  // Handle app installed event
  useEffect(() => {
    const handleAppInstalled = e => {
      console.log('PWA: App installed successfully');
      setIsInstalled(true);
      setIsVisible(false);
      setDeferredPrompt(null);
      onInstallSuccess?.(e);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [onInstallSuccess]);

  // Handle install button click
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('PWA: No deferred prompt available');
      return;
    }

    setIsInstalling(true);

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for user response
      const choiceResult = await deferredPrompt.userChoice;

      console.log('PWA: User choice:', choiceResult.outcome);

      if (choiceResult.outcome === 'accepted') {
        console.log('PWA: User accepted install');
        onInstallSuccess?.();
      } else {
        console.log('PWA: User dismissed install');
        onInstallCancel?.();
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setCanInstall(false);
      setIsVisible(false);
    } catch (error) {
      console.error('PWA: Install failed:', error);
      onInstallError?.(error);
    } finally {
      setIsInstalling(false);
    }
  };

  // Handle dismiss button click
  const handleDismiss = () => {
    setIsVisible(false);
    onInstallCancel?.();

    // Store dismissal in localStorage to avoid showing again soon
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Check if recently dismissed
  useEffect(() => {
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const timeSinceDismissal = Date.now() - parseInt(dismissedTime);
      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (timeSinceDismissal < oneDayInMs) {
        setIsVisible(false);
      }
    }
  }, []);

  // Get install prompt content based on device type
  const getInstallContent = () => {
    if (deviceType === 'mobile') {
      return {
        title: 'Install App',
        description: 'Add to your home screen for quick access and offline use',
        icon: '📱',
        buttonText: 'Install',
      };
    } else {
      return {
        title: 'Install Portfolio App',
        description: 'Install this app for a better experience and offline access',
        icon: '💻',
        buttonText: 'Install App',
      };
    }
  };

  // Don't render if conditions aren't met
  if (!canInstall || !isVisible || isInstalled) {
    return null;
  }

  const content = getInstallContent();

  return (
    <div
      className={classNames(
        'pwa-install-prompt',
        {
          'pwa-install-prompt--visible': isVisible,
          'pwa-install-prompt--mobile': deviceType === 'mobile',
          'pwa-install-prompt--desktop': deviceType === 'desktop',
          'pwa-install-prompt--installing': isInstalling,
        },
        className
      )}
      {...props}
    >
      <div className="pwa-install-prompt__content">
        <div className="pwa-install-prompt__icon">{content.icon}</div>

        <div className="pwa-install-prompt__text">
          <h3 className="pwa-install-prompt__title">{content.title}</h3>
          <p className="pwa-install-prompt__description">{content.description}</p>
        </div>

        <div className="pwa-install-prompt__actions">
          <Button
            className="pwa-install-prompt__install-button"
            onClick={handleInstallClick}
            disabled={isInstalling}
            loading={isInstalling}
            iconEnd={isInstalling ? null : 'arrowRight'}
            size="s"
          >
            {isInstalling ? 'Installing...' : content.buttonText}
          </Button>

          <button
            className="pwa-install-prompt__dismiss-button"
            onClick={handleDismiss}
            disabled={isInstalling}
            aria-label="Dismiss install prompt"
          >
            ×
          </button>
        </div>
      </div>

      {/* Background overlay for mobile */}
      {deviceType === 'mobile' && (
        <div className="pwa-install-prompt__overlay" onClick={handleDismiss} />
      )}
    </div>
  );
};

// Hook for managing PWA install state
export const usePWAInstall = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed
    setIsInstalled(isRunningAsPWA());

    // Listen for beforeinstallprompt
    const handleBeforeInstallPrompt = e => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;

      setDeferredPrompt(null);
      setIsInstallable(false);

      return choiceResult.outcome === 'accepted';
    } catch (error) {
      console.error('Install failed:', error);
      return false;
    }
  };

  return {
    isInstallable,
    isInstalled,
    install,
    canInstall: isInstallable && !isInstalled,
  };
};

export default PWAInstallPrompt;
